import type { SignatureTemplate } from "@/shared/types";
import { info, warn, debug } from "@/shared/services/logger";
import { flushRoaming } from "@/shared/services/storage";

const SOURCE = "tpl-roaming";
const CACHE_KEY = "helpitech_templates_cache";
const FETCHED_AT_KEY = "helpitech_templates_fetched_at";
const ROAMING_CACHE_KEY = "helpitech_tpl_cache";
const ROAMING_FETCHED_AT_KEY = "helpitech_tpl_at";
const ROAMING_MAX_BYTES = 12_000;

/**
 * Janela de validade do cache de templates. Passado esse tempo, o suplemento
 * rebusca as assinaturas da plataforma — é o que faz uma assinatura recém
 * atribuída a um grupo chegar ao Outlook sem o usuário precisar relogar.
 */
export const TEMPLATES_REFRESH_MS = 10 * 60 * 1000;

/**
 * Remove imagens base64 do HTML do template para economizar espaço no roaming settings.
 *
 * @param html - HTML do template original.
 * @returns HTML com tags img base64 substituídas por placeholder.
 *
 * @example
 * ```typescript
 * const clean = stripBase64Images('<img src="data:image/png;base64,ABC">');
 * // '<img alt="logo">'
 * ```
 */
function stripBase64Images(html: string): string {
  return html.replace(/<img([^>]*)src="data:image\/[^"]+"([^>]*)>/gi, '<img$1$2alt="logo">');
}

/**
 * Persiste os templates no roaming settings do Outlook para compartilhamento entre runtimes.
 *
 * @param templates - Lista de templates a salvar.
 *
 * @example
 * ```typescript
 * saveTemplatesToRoaming([{ id: 1, name: "Padrão", html: "<p>Sig</p>", is_default: true }]);
 * ```
 */
function saveTemplatesToRoaming(templates: SignatureTemplate[]): void {
  try {
    if (typeof Office === "undefined" || !Office.context?.roamingSettings) return;

    const minimal = templates.map((t) => ({
      id: t.id,
      name: t.name,
      is_default: t.is_default,
      mandatory: t.mandatory,
      html: stripBase64Images(t.html),
    }));

    for (const t of minimal) {
      debug(SOURCE, `[TEL-DEBUG] Template id=${t.id} (${t.name || "?"}): ${t.html.length} chars pós-stripBase64Images.`);
    }

    const json = JSON.stringify(minimal);
    info(SOURCE, `[TEL-DEBUG] Payload total: ${json.length} chars (limite=${ROAMING_MAX_BYTES}).`);
    if (json.length > ROAMING_MAX_BYTES) {
      warn(SOURCE, `[TEL-DEBUG] Payload (${json.length} chars) excede o limite de ${ROAMING_MAX_BYTES}  gravação no roaming abortada, launch event ficará só com o localStorage (não compartilhado) ou cache antigo.`);
      return;
    }

    Office.context.roamingSettings.set(ROAMING_CACHE_KEY, json);
    Office.context.roamingSettings.set(ROAMING_FETCHED_AT_KEY, Date.now());
    void flushRoaming().then((result) => {
      if (result.ok) {
        info(SOURCE, "[TEL-DEBUG] roamingSettings persistido OK.");
      } else {
        warn(SOURCE, `[TEL-DEBUG] roamingSettings falhou mesmo após descarte - code=${result.error?.code}, msg=${result.error?.message}.`);
      }
    });
  } catch (e: any) {
    warn(SOURCE, `[TEL-DEBUG] Não foi possível salvar templates no roaming: ${e?.message || e}`);
  }
}

export const templateCache = {
  /**
   * Recupera templates do localStorage.
   *
   * @returns Array de templates ou `null` se não encontrado.
   *
   * @example
   * ```typescript
   * const templates = templateCache.get();
   * ```
   */
  get(): SignatureTemplate[] | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? (JSON.parse(raw) as SignatureTemplate[]) : null;
    } catch {
      return null;
    }
  },

  /**
   * Salva templates no localStorage e, opcionalmente, no roaming settings.
   *
   * @param templates - Lista de templates a persistir.
   * @param shareToRoaming - `false` no runtime de background, que mantém cache
   * próprio no `localStorage` e nunca deve escrever no roaming.
   *
   * @example
   * ```typescript
   * templateCache.set(templates);                      // taskpane
   * templateCache.set(templates, false);               // background
   * ```
   */
  set(templates: SignatureTemplate[], shareToRoaming: boolean = true): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(templates));
      localStorage.setItem(FETCHED_AT_KEY, String(Date.now()));
    } catch (e) {
      console.warn("[Cache] Não foi possível salvar os templates:", e);
    }
    if (shareToRoaming) saveTemplatesToRoaming(templates);
  },

  /**
   * Momento da última busca bem-sucedida na plataforma.
   *
   * @returns Timestamp em ms, ou `0` se nunca buscou.
   *
   * @example
   * ```typescript
   * const idadeMs = Date.now() - templateCache.fetchedAt();
   * ```
   */
  fetchedAt(): number {
    try {
      const local = Number(localStorage.getItem(FETCHED_AT_KEY)) || 0;
      let roaming = 0;
      if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
        roaming = Number(Office.context.roamingSettings.get(ROAMING_FETCHED_AT_KEY)) || 0;
      }
      return Math.max(local, roaming);
    } catch {
      return 0;
    }
  },

  /**
   * Indica se o cache passou da janela de {@link TEMPLATES_REFRESH_MS}.
   *
   * @returns `true` quando os templates devem ser rebuscados.
   *
   * @example
   * ```typescript
   * if (templateCache.isStale()) await fetchSignatureTemplates(email, name);
   * ```
   */
  isStale(): boolean {
    return Date.now() - this.fetchedAt() > TEMPLATES_REFRESH_MS;
  },

  /**
   * Remove o cache de templates do localStorage e do roaming settings.
   *
   * @example
   * ```typescript
   * templateCache.clear();
   * ```
   */
  clear(): void {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(FETCHED_AT_KEY);
    try {
      if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
        Office.context.roamingSettings.remove(ROAMING_CACHE_KEY);
        Office.context.roamingSettings.remove(ROAMING_FETCHED_AT_KEY);
        void flushRoaming();
      }
    } catch (e) {
      console.warn("[Cache] Não foi possível limpar o cache de templates no roaming:", e);
    }
  }
};

export const TEMPLATE_SLOTS = ["new_mail", "reply", "forward"] as const;
export type TemplateSlot = (typeof TEMPLATE_SLOTS)[number];

/**
 * Monta o identificador de slot a partir do id do template.
 *
 * @param id - Id numérico do template.
 * @returns Chave no formato `template_<id>`.
 *
 * @example
 * templateKey(7); // "template_7"
 */
export function templateKey(id: number): string {
  return `template_${id}`;
}

/**
 * Extrai o id numérico de uma chave `template_<id>`.
 *
 * @param key - Valor lido de um slot (pode ser `"none"`, vazio ou inválido).
 * @returns O id, ou `null` quando a chave não referencia um template.
 *
 * @example
 * parseTemplateKey("template_7"); // 7
 * parseTemplateKey("none");       // null
 */
export function parseTemplateKey(key: unknown): number | null {
  if (typeof key !== "string") return null;
  const match = key.match(/^template_(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Localiza na lista o template referenciado por uma chave de slot.
 *
 * @param templates - Lista de templates disponíveis.
 * @param key - Valor do slot.
 * @returns Índice do template, ou `-1` se a chave não corresponde a nenhum.
 *
 * @example
 * indexOfTemplateKey(templates, "template_7");
 */
export function indexOfTemplateKey(
  templates: readonly SignatureTemplate[],
  key: unknown
): number {
  const id = parseTemplateKey(key);
  if (id === null) return -1;
  return templates.findIndex((t) => t.id === id);
}

/**
 * Retorna o template imposto pela organização, se houver.
 *
 * @param templates - Lista de templates disponíveis.
 * @returns O template `mandatory`, ou `undefined`.
 *
 * @example
 * const forcado = findMandatory(templates);
 */
export function findMandatory(
  templates: readonly SignatureTemplate[]
): SignatureTemplate | undefined {
  return templates.find((t) => t.mandatory);
}

/**
 * Grava um slot no `roamingSettings` e no espelho do `localStorage`.
 *
 * O espelho local é defesa em profundidade: `saveAsync` regrava o conteudo inteiro
 * do roaming a partir do snapshot do runtime que chamou, então uma gravação
 * vinda de outro runtime pode reverter os slots. Não faz flush  quem disparou
 * a alteração decide quando persistir.
 *
 * @param slot - Slot a gravar.
 * @param value - Chave `template_<id>` ou `"none"`.
 *
 * @example
 * setTemplateSlot("new_mail", templateKey(7));
 */
export function setTemplateSlot(slot: TemplateSlot, value: string): void {
  if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
    Office.context.roamingSettings.set(slot, value);
  }
  try {
    localStorage.setItem(slot, value);
  } catch {
    // localStorage indisponível  o roaming continua sendo a fonte principal.
  }
}

/**
 * Lê um slot, com o espelho do `localStorage` como fallback.
 *
 * @param slot - Slot a ler.
 * @returns Valor do slot, ou `""` quando nunca foi configurado.
 *
 * @example
 * getTemplateSlot("new_mail"); // "template_7"
 */
export function getTemplateSlot(slot: TemplateSlot): string {
  if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
    const roaming = Office.context.roamingSettings.get(slot);
    if (typeof roaming === "string" && roaming !== "") return roaming;
  }
  try {
    return localStorage.getItem(slot) || "";
  } catch {
    return "";
  }
}

/**
 * Alinha os slots de assinatura com a lista de templates realmente disponível.
 *
 * Não chama `saveAsync`: quem disparou a reconciliação persiste, para evitar
 * gravações concorrentes no `roamingSettings`.
 *
 * @param templates - Lista viva de templates do usuário.
 * @returns `true` se algum slot mudou.
 *
 * @example
 * ```typescript
 * if (reconcileTemplateSlots(templates)) await flushRoaming();
 * ```
 */
export function reconcileTemplateSlots(templates: readonly SignatureTemplate[]): boolean {
  if (typeof Office === "undefined" || !Office.context?.roamingSettings) return false;

  if (templates.length === 0) return false;

  const mandatory = findMandatory(templates);
  const fallback = templates.find((t) => t.is_default)
    ?? (templates.length === 1 ? templates[0] : undefined);

  let changed = false;

  for (const slot of TEMPLATE_SLOTS) {
    const effectiveValue = getTemplateSlot(slot);
    const roamingValue = Office.context.roamingSettings.get(slot);

    let nextValue: string;
    if (mandatory) {
      nextValue = templateKey(mandatory.id);
    } else if (indexOfTemplateKey(templates, effectiveValue) >= 0) {
      nextValue = effectiveValue; // escolha do usuário ainda é válida
    } else {
      nextValue = fallback ? templateKey(fallback.id) : "none";
    }

    if (nextValue !== effectiveValue || nextValue !== roamingValue) {
      setTemplateSlot(slot, nextValue);
      changed = true;
    }
  }

  if (changed) {
    info(SOURCE, `reconcileTemplateSlots: slots atualizados (mandatory=${mandatory?.id ?? "não"}).`);
  }
  return changed;
}